import React, {
  memo, useState, useEffect, useRef, useCallback,
} from '../../../lib/teact/teact';
import Icon from '../../common/icons/Icon';
import buildClassName from '../../../util/buildClassName';
import { 
  AuditMockupCard, AnalyticsMockupCard, DraggablePillMockup, AdminBadgeMockup, BotBadgeMockup
} from './LarpersLandingFeatures';
import tokenPillStyles from '../../common/crypto/TokenPill.module.scss';
import landingStyles from '../LarpersLanding.module.scss';
import styles from './PwaPreviewScreen.module.scss';

type OwnProps = {
  onLoginClick: () => void;
};

const LiveTokenPill = memo(() => {
  const [mcap, setMcap] = useState(45.0);
  const [change, setChange] = useState(456.2);

  useEffect(() => {
    const interval = setInterval(() => {
      // Randomly fluctuate mcap and change by a small amount
      const mcapDelta = (Math.random() - 0.4) * 0.5; // Slight upward bias
      const changeDelta = (Math.random() - 0.4) * 2.0;

      setMcap((prev) => Math.max(1, prev + mcapDelta));
      setChange((prev) => prev + changeDelta);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className={tokenPillStyles.pillWrapper}>
      <span className={tokenPillStyles.pill} style={'cursor: default'}>
        <img className={tokenPillStyles.icon} src="pill-logo.png" alt="logo" style={'object-fit: cover;'} />
        <span className={tokenPillStyles.ticker}>Pepe {mcap.toFixed(1)}k</span>
        <span className={buildClassName(tokenPillStyles.change, change >= 0 ? tokenPillStyles.positive : tokenPillStyles.negative)} style={'opacity: 1'}>
          {change >= 0 ? '+' : ''}{change.toFixed(1)}%
        </span>
      </span>
    </span>
  );
});

const PwaPreviewScreen = ({ onLoginClick }: OwnProps) => {
  const carouselRef = useRef<HTMLDivElement>();
  const [activeSlide, setActiveSlide] = useState(0);
  const [hasSwiped, setHasSwiped] = useState(false);

  const handleScroll = useCallback(() => {
    if (!carouselRef.current) return;
    const scrollLeft = carouselRef.current.scrollLeft;
    const width = carouselRef.current.offsetWidth;
    const newSlide = Math.round(scrollLeft / width);
    setActiveSlide(newSlide);
    if (newSlide > 0) {
      setHasSwiped(true);
    }
  }, []);

  return (
    <div className={styles.root}>
      {/* ── CAROUSEL CONTENT ── */}
      <div className={styles.carousel} ref={carouselRef} onScroll={handleScroll}>
        
        {/* SLIDE 1: Message Bubble */}
        <div className={styles.slide}>
          <div className={landingStyles.messageBubble} style={'margin: 0; align-self: flex-start; max-width: 90%; background: #212121;'}>
            <p className={landingStyles.messageText} style={'margin-bottom: 8px;'}>
              Guys i feel this can moon
            </p>
            
            <div className={landingStyles.messagePillWrapper}>
               <div className={styles.pillWrapper}>
                 <LiveTokenPill />
               </div>
            </div>

            <div className={landingStyles.messageMeta}>
              <span className={landingStyles.messageTime}>13:37</span>
              <span className={landingStyles.messageChecks}>
                <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.875 3.5l-4.75 4.75-2.25-2.25L2.8125 7l3.3125 3.3125 5.8125-5.8125-1.0625-1z M14.125 3.5l-4.75 4.75-0.75-0.75 1.0625-1.0625 3.375-3.375 1.0625 1.0625z" fill="currentColor" />
                  <path d="M9.375 8.25l-0.5625 0.5625 1.5 1.5 5.8125-5.8125-1.0625-1.0625-4.75 4.75-0.9375-0.9375z" fill="currentColor" />
                </svg>
              </span>
            </div>
          </div>

          <p className={styles.featuresText}>
            No ugly addresses. Just live data for instant decision making and trades.
          </p>

          {!hasSwiped && (
            <div className={styles.swipeHintContainer}>
               <span className={styles.swipeHintText}>Swipe to explore</span>
               <Icon name={"next" as any} className={styles.swipeHintIcon} />
            </div>
          )}
        </div>

        {/* SLIDE 2: 13% Top Sheet Mockup */}
        <div className={styles.slide}>
          <div className={landingStyles.mockupPillCard} style={'margin: auto 0 1rem 0; width: 100%; box-sizing: border-box;'}>
            <div className={landingStyles.dragHandle} />
            <div style={'display: flex; align-items: center; justify-content: space-between; width: 100%;'}>
              <div style={'display: flex; align-items: center; gap: 0.75rem; flex: 1; min-width: 0;'}>
                <img src="pill-logo.png" alt="coin" className={landingStyles.avatarImage} />
                <div style={'display: flex; flex-direction: column; gap: 0.125rem;'}>
                  <div style={'display: flex; align-items: center; gap: 0.375rem;'}>
                    <span style={'font-size: 1rem; font-weight: 600; color: #fff;'}>Pepe</span>
                    <span className={landingStyles.ageIcon}>🍼</span>
                    <div className={landingStyles.socialIcons}>
                      <span className={landingStyles.socialLink}>
                        <Icon name={"telegram" as any} className={landingStyles.socialIcon} />
                      </span>
                      <span className={landingStyles.socialLink}>
                        <Icon name={"twitter" as any} className={buildClassName(landingStyles.socialIcon, landingStyles.xIcon)} />
                      </span>
                    </div>
                  </div>
                  <div style={'display: flex; align-items: center; gap: 0.375rem; font-size: 0.75rem;'}>
                    <span style={'color: rgba(255, 255, 255, 0.4);'}>Eqd23...29a</span>
                    <Icon name="copy" style={'font-size: 0.75rem; color: rgba(255, 255, 255, 0.4);'} />
                  </div>
                </div>
              </div>

              <div className={landingStyles.mcapCol}>
                <div className={landingStyles.mcapValueWrapper}>
                  <span className={landingStyles.mcapLabel}>MCAP</span>
                  <span className={landingStyles.mcapValue}>$45.0K</span>
                </div>
                <div className={landingStyles.quickBuyRow}>
                  <button type="button" className={landingStyles.quickBuyBtn}>Buy 0.5T</button>
                  <button type="button" className={landingStyles.quickBuyBtn}>Buy 1T</button>
                </div>
              </div>
            </div>
          </div>

          <p className={styles.featuresText}>
            Floats on top of chat, lets you monitor any coin and trade without switching apps.
          </p>
        </div>

        {/* SLIDE 3: Analytics Mockup */}
        <div className={styles.slide}>
          <div style={'width: 100%; box-sizing: border-box;'}>
            <AnalyticsMockupCard />
          </div>
          <p className={styles.featuresText}>
            Find who is real insider, not just one group but global. See how dev push project to plan entry and exit !
          </p>
        </div>

        {/* SLIDE 4: Audit Mockup */}
        <div className={styles.slide}>
          <div style={'width: 100%; box-sizing: border-box;'}>
            <AuditMockupCard />
          </div>
          <p className={styles.featuresText}>
            When others see outdated bot slow data, you moinitor mili sec accurate cluster and other info
          </p>
        </div>

        {/* SLIDE 5: Draggable Pill Mockup */}
        <div className={styles.slide}>
          <div style={'display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: 1rem; width: 100%; box-sizing: border-box; margin: auto 0 1rem 0;'}>
            <p className={styles.featuresText} style={'margin: 0; flex: 1;'}>
              Drag down to snap your bag and release to sell instantly in milliseconds.
            </p>
            <div style={'flex-shrink: 0;'}>
              <DraggablePillMockup />
            </div>
          </div>
        </div>

        {/* SLIDE 6: Admin Badge Mockup */}
        <div className={styles.slide}>
          <div style={'width: 100%; box-sizing: border-box;'}>
            <AdminBadgeMockup />
          </div>
          <p className={styles.featuresText}>
            Easily track if the devs are actually alive, building, or if they've left the chat.
          </p>
        </div>

        {/* SLIDE 7: Bot Badge Mockup */}
        <div className={styles.slide}>
          <div style={'width: 100%; box-sizing: border-box;'}>
            <BotBadgeMockup />
          </div>
          <p className={styles.featuresText}>
            Hide the spam and keep your feed clean from bots dumping ugly, slow data.
          </p>
        </div>

      </div>

      {/* Pagination Dots */}
      <div className={styles.dots}>
        <div className={buildClassName(styles.dot, activeSlide === 0 && styles.dotActive)} />
        <div className={buildClassName(styles.dot, activeSlide === 1 && styles.dotActive)} />
        <div className={buildClassName(styles.dot, activeSlide === 2 && styles.dotActive)} />
        <div className={buildClassName(styles.dot, activeSlide === 3 && styles.dotActive)} />
        <div className={buildClassName(styles.dot, activeSlide === 4 && styles.dotActive)} />
        <div className={buildClassName(styles.dot, activeSlide === 5 && styles.dotActive)} />
        <div className={buildClassName(styles.dot, activeSlide === 6 && styles.dotActive)} />
      </div>

      {/* ── BOTTOM LOGIN PROMPT ── */}
      <div className={styles.loginPrompt}>
        <p className={styles.loginPromptText}>Login to see your real chats & trade live</p>
        <button type="button" className={styles.loginPromptBtn} onClick={onLoginClick}>
          <Icon name={"telegram" as any} className={styles.telegramIcon} />
          Continue with Telegram
        </button>
      </div>
    </div>
  );
};

export default memo(PwaPreviewScreen);
